/**
 * The health state of one endpoint, as a dot plus a word.
 *
 * The word is not decoration. `unknown` and `healthy` are both "not red", and a
 * colour-only badge would let a reader skim a screen of grey dots and conclude
 * the API is fine when in fact we have no data at all about those endpoints.
 * Every badge carries the state's hint in its tooltip for the same reason.
 */
import { useI18n } from '../i18n'
import type { HealthState } from '../api/types'
import { HEALTH_HINT_KEYS, HEALTH_LABEL_KEYS, HEALTH_STYLES } from './statusColors'

export interface HealthBadgeProps {
  state: HealthState
  /** Dot only, for dense contexts that already label the state elsewhere. */
  compact?: boolean
}

export function HealthBadge({ state, compact = false }: HealthBadgeProps) {
  const { t } = useI18n()
  const style = HEALTH_STYLES[state]
  const label = t(HEALTH_LABEL_KEYS[state])
  return (
    <span
      data-testid={`health-badge-${state}`}
      title={t(HEALTH_HINT_KEYS[state])}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-fg"
    >
      <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${style.bg}`} />
      {compact ? <span className="sr-only">{label}</span> : label}
    </span>
  )
}
